import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  Settings,
  FolderTree,
  ChevronRight,
  ChevronDown,
  Loader2,
  GripVertical,
  FileText,
  Copy,
  Save,
  Layers
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sortable Category Item Component
const SortableCategoryItem = ({ category, onEdit, onDelete, onFields, onSettings, onToggleExpand, isExpanded, level = 0 }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div
        className={`flex items-center gap-2 p-3 bg-white border rounded-lg mb-2 hover:shadow-md transition-shadow ${
          isDragging ? 'shadow-lg' : ''
        }`}
        style={{ marginLeft: `${level * 24}px` }}
      >
        {/* Drag Handle */}
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded">
          <GripVertical className="h-4 w-4 text-gray-400" />
        </div>

        {/* Expand/Collapse for parent categories */}
        {category.children && category.children.length > 0 ? (
          <button onClick={() => onToggleExpand(category.id)} className="p-1 hover:bg-gray-100 rounded">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        ) : (
          <div className="w-6" />
        )}

        {/* Icon */}
        <span className="text-2xl">{category.icon || '📁'}</span>

        {/* Name & Info */}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">{category.name}</span>
            <Badge variant={category.is_active ? 'default' : 'secondary'} className="text-xs">
              {category.is_active ? 'Active' : 'Inactive'}
            </Badge>
            {category.children && category.children.length > 0 && (
              <span className="text-xs text-gray-500">
                ({category.children.length} subcategories)
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500">{category.slug}</p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => onFields(category)} title="Custom Fields">
            <FileText className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onSettings(category)} title="Settings">
            <Settings className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onEdit(category)} title="Edit">
            <Edit className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => onDelete(category.id, category.name)} title="Delete">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Children */}
      {isExpanded && category.children && category.children.length > 0 && (
        <div className="ml-6">
          {category.children.map((child) => (
            <SortableCategoryItem
              key={child.id}
              category={child}
              onEdit={onEdit}
              onDelete={onDelete}
              onFields={onFields}
              onSettings={onSettings}
              onToggleExpand={onToggleExpand}
              isExpanded={false}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CategoryManagement = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [activeTab, setActiveTab] = useState('categories');

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFieldsModal, setShowFieldsModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showApplyTemplateModal, setShowApplyTemplateModal] = useState(false);

  // Selected items
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customFields, setCustomFields] = useState([]);

  // Forms
  const [categoryForm, setCategoryForm] = useState({
    name: '', slug: '', description: '', parent_id: null, icon: '', is_active: true
  });
  const [fieldForm, setFieldForm] = useState({
    name: '', label: '', field_type: 'text', required: false, options: '', placeholder: '', help_text: ''
  });
  const [settingsForm, setSettingsForm] = useState({
    posting_permission: 'all', posting_type: 'free', posting_price: 0,
    ad_duration_days: 30, max_images: 5, auto_approval: false
  });
  const [templateForm, setTemplateForm] = useState({
    name: '', description: '', fields: []
  });

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchCategories();
    fetchTemplates();
  }, [navigate]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/categories/?include_inactive=true`, { headers: getAuthHeader() });
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/categories/field-templates`, { headers: getAuthHeader() });
      setTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  // Flatten categories for drag-and-drop
  const flattenForDnD = (cats, result = []) => {
    cats.forEach(cat => {
      result.push(cat);
      if (cat.children && expandedCategories[cat.id]) {
        flattenForDnD(cat.children, result);
      }
    });
    return result;
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const flatCategories = flattenForDnD(categories);
    const oldIndex = flatCategories.findIndex(c => c.id === active.id);
    const newIndex = flatCategories.findIndex(c => c.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(flatCategories, oldIndex, newIndex);
      
      // Update orders in backend
      try {
        const orders = newOrder.map((cat, index) => ({
          id: cat.id,
          order: index,
          parent_id: cat.parent_id
        }));

        await axios.post(`${API_URL}/api/admin/categories/bulk-reorder`, orders, { headers: getAuthHeader() });
        fetchCategories();
      } catch (err) {
        console.error('Error reordering:', err);
        alert('Failed to reorder categories');
      }
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/categories/`, categoryForm, { headers: getAuthHeader() });
      alert('Category created successfully');
      setShowAddModal(false);
      setCategoryForm({ name: '', slug: '', description: '', parent_id: null, icon: '', is_active: true });
      fetchCategories();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/admin/categories/${selectedCategory.id}`, categoryForm, { headers: getAuthHeader() });
      alert('Category updated successfully');
      setShowEditModal(false);
      fetchCategories();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update category');
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Delete "${categoryName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/categories/${categoryId}`, { headers: getAuthHeader() });
      alert('Category deleted');
      fetchCategories();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete category');
    }
  };

  const openEditModal = (category) => {
    setSelectedCategory(category);
    setCategoryForm({
      name: category.name, slug: category.slug, description: category.description || '',
      parent_id: category.parent_id || null, icon: category.icon || '', is_active: category.is_active
    });
    setShowEditModal(true);
  };

  const openFieldsModal = async (category) => {
    setSelectedCategory(category);
    try {
      const response = await axios.get(`${API_URL}/api/admin/categories/${category.id}`, { headers: getAuthHeader() });
      setCustomFields(response.data.custom_fields || []);
      setShowFieldsModal(true);
    } catch (err) {
      alert('Failed to load custom fields');
    }
  };

  const openSettingsModal = async (category) => {
    setSelectedCategory(category);
    try {
      const response = await axios.get(`${API_URL}/api/admin/categories/${category.id}/settings`, { headers: getAuthHeader() });
      setSettingsForm({
        posting_permission: response.data.posting_permission || 'all',
        posting_type: response.data.posting_type || 'free',
        posting_price: response.data.posting_price || 0,
        ad_duration_days: response.data.ad_duration_days || 30,
        max_images: response.data.max_images || 5,
        auto_approval: response.data.auto_approval || false
      });
      setShowSettingsModal(true);
    } catch (err) {
      alert('Failed to load settings');
    }
  };

  const handleAddCustomField = async (e) => {
    e.preventDefault();
    try {
      const fieldData = {
        ...fieldForm,
        options: fieldForm.options ? fieldForm.options.split(',').map(o => o.trim()) : null
      };
      await axios.post(`${API_URL}/api/admin/categories/${selectedCategory.id}/custom-fields`, fieldData, { headers: getAuthHeader() });
      alert('Field added');
      setFieldForm({ name: '', label: '', field_type: 'text', required: false, options: '', placeholder: '', help_text: '' });
      openFieldsModal(selectedCategory);
    } catch (err) {
      alert('Failed to add field');
    }
  };

  const handleDeleteField = async (fieldId) => {
    if (!window.confirm('Delete this field?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/categories/${selectedCategory.id}/custom-fields/${fieldId}`, { headers: getAuthHeader() });
      openFieldsModal(selectedCategory);
    } catch (err) {
      alert('Failed to delete field');
    }
  };

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/admin/categories/${selectedCategory.id}/settings`, settingsForm, { headers: getAuthHeader() });
      alert('Settings updated');
      setShowSettingsModal(false);
    } catch (err) {
      alert('Failed to update settings');
    }
  };

  // Template functions
  const handleSaveAsTemplate = async () => {
    if (customFields.length === 0) {
      alert('No fields to save as template');
      return;
    }
    const name = prompt('Enter template name:');
    if (!name) return;

    try {
      await axios.post(`${API_URL}/api/admin/categories/field-templates`, {
        name,
        description: `Template from ${selectedCategory.name}`,
        fields: customFields.map(f => ({
          name: f.name, label: f.label, field_type: f.field_type,
          required: f.required, options: f.options, placeholder: f.placeholder, help_text: f.help_text
        }))
      }, { headers: getAuthHeader() });
      alert('Template saved!');
      fetchTemplates();
    } catch (err) {
      alert('Failed to save template');
    }
  };

  const handleApplyTemplate = async (templateId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/categories/${selectedCategory.id}/apply-template/${templateId}`,
        {},
        { headers: getAuthHeader() }
      );
      alert(response.data.message);
      openFieldsModal(selectedCategory);
      setShowApplyTemplateModal(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to apply template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/categories/field-templates/${templateId}`, { headers: getAuthHeader() });
      fetchTemplates();
    } catch (err) {
      alert('Failed to delete template');
    }
  };

  const toggleExpand = (categoryId) => {
    setExpandedCategories(prev => ({ ...prev, [categoryId]: !prev[categoryId] }));
  };

  // Flatten for parent select
  const flattenForSelect = (cats, result = [], prefix = '') => {
    cats.forEach(cat => {
      result.push({ id: cat.id, name: prefix + cat.name });
      if (cat.children) flattenForSelect(cat.children, result, prefix + '- ');
    });
    return result;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin/dashboard')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Category Management</h1>
                <p className="text-gray-600 text-sm">Drag to reorder • Manage fields & settings</p>
              </div>
            </div>
            <Button onClick={() => setShowAddModal(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="categories">
              <FolderTree className="h-4 w-4 mr-2" />
              Categories ({categories.length})
            </TabsTrigger>
            <TabsTrigger value="templates">
              <Layers className="h-4 w-4 mr-2" />
              Field Templates ({templates.length})
            </TabsTrigger>
          </TabsList>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GripVertical className="h-5 w-5 text-gray-400" />
                  Drag & Drop to Reorder
                </CardTitle>
              </CardHeader>
              <CardContent>
                {categories.length > 0 ? (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={flattenForDnD(categories).map(c => c.id)} strategy={verticalListSortingStrategy}>
                      {categories.map((category) => (
                        <SortableCategoryItem
                          key={category.id}
                          category={category}
                          onEdit={openEditModal}
                          onDelete={handleDeleteCategory}
                          onFields={openFieldsModal}
                          onSettings={openSettingsModal}
                          onToggleExpand={toggleExpand}
                          isExpanded={expandedCategories[category.id]}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                ) : (
                  <div className="text-center py-12">
                    <FolderTree className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No categories yet</p>
                    <Button onClick={() => setShowAddModal(true)} className="mt-4">
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Category
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Field Templates</CardTitle>
                  <Button onClick={() => { setTemplateForm({ name: '', description: '', fields: [] }); setShowTemplateModal(true); }}>
                    <Plus className="h-4 w-4 mr-2" />
                    New Template
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {templates.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {templates.map((template) => (
                      <Card key={template.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="font-semibold">{template.name}</h3>
                              <p className="text-sm text-gray-500">{template.description}</p>
                            </div>
                            <Badge>{template.fields?.length || 0} fields</Badge>
                          </div>
                          <div className="flex gap-2 mt-4">
                            <Button size="sm" variant="outline" onClick={() => { setSelectedTemplate(template); setTemplateForm(template); setShowTemplateModal(true); }}>
                              <Edit className="h-3 w-3 mr-1" />
                              Edit
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteTemplate(template.id)}>
                              <Trash2 className="h-3 w-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Layers className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No templates yet. Save custom fields from a category as a template!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Category Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Category</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddCategory} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })} required />
              </div>
              <div>
                <Label>Slug *</Label>
                <Input value={categoryForm.slug} onChange={(e) => setCategoryForm({ ...categoryForm, slug: e.target.value })} required />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={categoryForm.description} onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Icon (Emoji)</Label>
                <Input value={categoryForm.icon} onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })} placeholder="🚗" />
              </div>
              <div>
                <Label>Parent Category</Label>
                <Select value={categoryForm.parent_id || 'none'} onValueChange={(v) => setCategoryForm({ ...categoryForm, parent_id: v === 'none' ? null : v })}>
                  <SelectTrigger><SelectValue placeholder="None (Top Level)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Top Level)</SelectItem>
                    {flattenForSelect(categories).map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Switch checked={categoryForm.is_active} onCheckedChange={(v) => setCategoryForm({ ...categoryForm, is_active: v })} />
              <Label>Active</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button type="submit">Create Category</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Category Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateCategory} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} required />
              </div>
              <div>
                <Label>Slug *</Label>
                <Input value={categoryForm.slug} onChange={(e) => setCategoryForm({ ...categoryForm, slug: e.target.value })} required />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={categoryForm.description} onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Icon (Emoji)</Label>
                <Input value={categoryForm.icon} onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })} />
              </div>
              <div>
                <Label>Parent Category</Label>
                <Select value={categoryForm.parent_id || 'none'} onValueChange={(v) => setCategoryForm({ ...categoryForm, parent_id: v === 'none' ? null : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Top Level)</SelectItem>
                    {flattenForSelect(categories).filter(c => c.id !== selectedCategory?.id).map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Switch checked={categoryForm.is_active} onCheckedChange={(v) => setCategoryForm({ ...categoryForm, is_active: v })} />
              <Label>Active</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
              <Button type="submit">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Custom Fields Modal */}
      <Dialog open={showFieldsModal} onOpenChange={setShowFieldsModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Custom Fields - {selectedCategory?.name}</DialogTitle>
            <DialogDescription>Define fields that sellers must fill when posting in this category</DialogDescription>
          </DialogHeader>

          {/* Template Actions */}
          <div className="flex gap-2 mb-4">
            <Button variant="outline" size="sm" onClick={handleSaveAsTemplate} disabled={customFields.length === 0}>
              <Save className="h-4 w-4 mr-1" />
              Save as Template
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowApplyTemplateModal(true)}>
              <Copy className="h-4 w-4 mr-1" />
              Apply Template
            </Button>
          </div>

          {/* Existing Fields */}
          {customFields.length > 0 && (
            <div className="mb-6">
              <h4 className="font-semibold mb-2">Current Fields ({customFields.length})</h4>
              <div className="space-y-2">
                {customFields.map((field) => (
                  <div key={field.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <span className="font-medium">{field.label}</span>
                      <span className="text-gray-500 text-sm ml-2">({field.field_type})</span>
                      {field.required && <Badge variant="destructive" className="ml-2 text-xs">Required</Badge>}
                    </div>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteField(field.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add Field Form */}
          <form onSubmit={handleAddCustomField} className="space-y-4 border-t pt-4">
            <h4 className="font-semibold">Add New Field</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Field Name *</Label>
                <Input value={fieldForm.name} onChange={(e) => setFieldForm({ ...fieldForm, name: e.target.value })} placeholder="brand" required />
              </div>
              <div>
                <Label>Label *</Label>
                <Input value={fieldForm.label} onChange={(e) => setFieldForm({ ...fieldForm, label: e.target.value })} placeholder="Brand Name" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Field Type</Label>
                <Select value={fieldForm.field_type} onValueChange={(v) => setFieldForm({ ...fieldForm, field_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="dropdown">Dropdown</SelectItem>
                    <SelectItem value="checkbox">Checkbox</SelectItem>
                    <SelectItem value="radio">Radio Buttons</SelectItem>
                    <SelectItem value="date">Date</SelectItem>
                    <SelectItem value="textarea">Text Area</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Placeholder</Label>
                <Input value={fieldForm.placeholder} onChange={(e) => setFieldForm({ ...fieldForm, placeholder: e.target.value })} />
              </div>
            </div>
            {['dropdown', 'radio'].includes(fieldForm.field_type) && (
              <div>
                <Label>Options (comma separated)</Label>
                <Input value={fieldForm.options} onChange={(e) => setFieldForm({ ...fieldForm, options: e.target.value })} placeholder="Option 1, Option 2, Option 3" />
              </div>
            )}
            <div>
              <Label>Help Text</Label>
              <Input value={fieldForm.help_text} onChange={(e) => setFieldForm({ ...fieldForm, help_text: e.target.value })} />
            </div>
            <div className="flex items-center space-x-2">
              <Switch checked={fieldForm.required} onCheckedChange={(v) => setFieldForm({ ...fieldForm, required: v })} />
              <Label>Required field</Label>
            </div>
            <Button type="submit">
              <Plus className="h-4 w-4 mr-2" />
              Add Field
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Apply Template Modal */}
      <Dialog open={showApplyTemplateModal} onOpenChange={setShowApplyTemplateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply Field Template</DialogTitle>
            <DialogDescription>Select a template to add its fields to {selectedCategory?.name}</DialogDescription>
          </DialogHeader>
          {templates.length > 0 ? (
            <div className="space-y-2">
              {templates.map((template) => (
                <div key={template.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                  <div>
                    <p className="font-medium">{template.name}</p>
                    <p className="text-sm text-gray-500">{template.fields?.length || 0} fields</p>
                  </div>
                  <Button size="sm" onClick={() => handleApplyTemplate(template.id)}>Apply</Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-4">No templates available</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Category Settings - {selectedCategory?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateSettings} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Who can post?</Label>
                <Select value={settingsForm.posting_permission} onValueChange={(v) => setSettingsForm({ ...settingsForm, posting_permission: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="verified">Verified Users Only</SelectItem>
                    <SelectItem value="premium">Premium Members Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Posting Type</Label>
                <Select value={settingsForm.posting_type} onValueChange={(v) => setSettingsForm({ ...settingsForm, posting_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {settingsForm.posting_type === 'paid' && (
              <div>
                <Label>Posting Price (Rs)</Label>
                <Input type="number" value={settingsForm.posting_price} onChange={(e) => setSettingsForm({ ...settingsForm, posting_price: parseFloat(e.target.value) })} />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ad Duration (days)</Label>
                <Input type="number" value={settingsForm.ad_duration_days} onChange={(e) => setSettingsForm({ ...settingsForm, ad_duration_days: parseInt(e.target.value) })} />
              </div>
              <div>
                <Label>Max Images</Label>
                <Input type="number" value={settingsForm.max_images} onChange={(e) => setSettingsForm({ ...settingsForm, max_images: parseInt(e.target.value) })} />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Switch checked={settingsForm.auto_approval} onCheckedChange={(v) => setSettingsForm({ ...settingsForm, auto_approval: v })} />
              <Label>Auto-approve ads in this category</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowSettingsModal(false)}>Cancel</Button>
              <Button type="submit">Save Settings</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoryManagement;
